"use client";

import { motion } from "framer-motion";
import StatisticsSection from "../HomePage/StatisticsSection";
import MissionVisionValues from "./MissionVisionValues";
import MilestonesAchievements from "./MilestonesAchievements";
import TeamSection from "../HomePage/TeamSection";
import HowItWorks from "../HomePage/HowItWorks";
import { useEffect } from "react";
import { useTheme } from "../../Components/ThemeContext";
import SEOHead from "../../Components/SEOHead";

// ─── JSON-LD Schema for About page ───────────────────────────────────────────
const aboutSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "About Fiieve — Software & Engineering Company",
  "url": "https://fiieve.com/about-us",
  "description":
    "Fiieve is a UK-based software and engineering company delivering web development, mobile apps, AI automation, IoT, and embedded systems for startups and enterprises.",
  "publisher": {
    "@type": "Organization",
    "name": "Fiieve",
    "url": "https://fiieve.com",
    "logo": "https://fiieve.com/Images/logo.png",
    "sameAs": [
      "https://twitter.com/fiieve",
      "https://www.linkedin.com/company/fiieve",
      "https://www.instagram.com/fiieve"
    ]
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://fiieve.com/" },
      { "@type": "ListItem", "position": 2, "name": "About Us", "item": "https://fiieve.com/about-us" }
    ]
  }
});

const lightGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

const darkGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(251,146,60,0.05) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

export default function AboutUsSection() {
  const { isDark } = useTheme();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const text      = isDark ? "text-white"     : "text-gray-900";
  const greenText = isDark ? "text-green-400" : "text-green-700";
  const bodyText  = isDark ? "text-gray-300"  : "text-gray-700";

  return (
    <>
      {/* ───── SEO Head ───── */}
      <SEOHead
        title="About Fiieve | Software & Engineering Company UK"
        description="Learn about Fiieve — a UK-based software and engineering company. We deliver web development, mobile apps, AI automation, IoT, and embedded systems for startups and enterprises."
        canonical="https://fiieve.com/about-us"
        ogImage="https://fiieve.com/Images/og-about.jpg"
        schema={aboutSchema}
      />

      <main
        aria-label="About Fiieve"
        className={`relative overflow-hidden pt-20 sm:pt-24 lg:pt-28 transition-colors duration-300 ${
          isDark ? "bg-black text-white" : "bg-[#f0f4f0] text-gray-900"
        }`}
        style={isDark ? darkGridStyle : lightGridStyle}
      >
        {/* Light mode atmospheric glows */}
        {!isDark && (
          <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
            <div style={{
              position: "absolute", top: "5%", left: "-8%",
              width: "45vw", height: "45vw",
              background: "radial-gradient(circle, rgba(74,222,128,0.11) 0%, transparent 70%)",
              filter: "blur(45px)",
            }} />
            <div style={{
              position: "absolute", top: "5%", right: "-8%",
              width: "40vw", height: "40vw",
              background: "radial-gradient(circle, rgba(251,146,60,0.09) 0%, transparent 70%)",
              filter: "blur(45px)",
            }} />
            <div style={{
              position: "absolute", top: "50%", left: "20%",
              width: "60vw", height: "40vw",
              background: "radial-gradient(ellipse, rgba(74,222,128,0.07) 0%, transparent 65%)",
              filter: "blur(55px)",
            }} />
          </div>
        )}

        <div className="relative" style={{ zIndex: 1 }}>

          {/* ===== Hero Banner ===== */}
          {/* ✅ SEO: H1 with primary keyword — only one H1 per page */}
          <div className="relative h-[25vh] overflow-hidden" style={{ marginBottom: "-3px" }}>
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: "url('/Images/bg.png')" }}
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-black/80" aria-hidden="true" />
            <div className="relative mx-auto max-w-7xl px-6 md:px-8 h-full flex flex-col items-center justify-center text-center">
              <h1 className="text-3xl md:text-5xl font-extrabold text-green-400">
                About Fiieve — Software & Engineering Company
              </h1>
              {/* ✅ SEO: BreadcrumbList visible to users + matches JSON-LD above */}
              <nav aria-label="Breadcrumb" className="mt-3">
                <ol className="flex items-center justify-center gap-1 text-sm text-white">
                  <li><a href="/" className="hover:text-green-400 transition-colors">Home</a></li>
                  <li aria-hidden="true" className="text-gray-400">/</li>
                  <li aria-current="page" className="text-green-400">About Us</li>
                </ol>
              </nav>
            </div>
          </div>

          {/* ===== Our Story Section ===== */}
          {/* ✅ SEO: <article> wraps original, standalone content — boosts EEAT */}
          <article
            aria-labelledby="our-story-heading"
            className="mx-auto max-w-7xl px-6 md:px-8 pt-8 pb-16 -mt-2"
          >
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true }}
              className="text-center"
            >
              {/* ✅ SEO: keyword-rich label above H2 */}
              <p className={`text-sm font-semibold uppercase tracking-widest ${greenText} mb-1`}>
                Who We Are
              </p>
              {/* ✅ SEO: H2 — section heading, secondary keyword embedded */}
              <h2
                id="our-story-heading"
                className={`mt-2 text-3xl md:text-5xl font-extrabold leading-tight ${text}`}
              >
                We build software that works for you —{" "}
                <span className={greenText}>not the other way around.</span>
              </h2>
            </motion.div>

            {/* Row 1: Text Left | Image Right */}
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <motion.div
                initial={{ opacity: 0, x: -80 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true }}
                className={`${bodyText} leading-relaxed text-base md:text-lg`}
              >
                <p>
                  At <strong className={`${greenText} font-semibold`}>Fiieve</strong>, we deliver
                  digital solutions for real business problems. Our team blends creativity and
                  technical expertise to craft powerful software — from{" "}
                  <strong>web development</strong> and <strong>mobile apps</strong> to{" "}
                  <strong>AI automation</strong> and <strong>IoT systems</strong> — that drives
                  growth, efficiency, and innovation for every client.
                </p>
                <p className="mt-4">
                  We partner closely with stakeholders to understand goals and constraints, then
                  translate them into reliable, scalable systems. From discovery to delivery, we
                  focus on outcomes, simplicity, and sustainability — so your software works the
                  way your business needs it to, today and as you grow.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 80 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true }}
                className="flex justify-center"
              >
                <img
                  src="/Images/developer.png"
                  alt="Fiieve software development team collaborating on web and AI projects"
                  className="w-full max-w-md rounded-lg border border-green-400/20 shadow-lg object-cover md:h-[260px] sm:h-[220px] h-[180px]"
                  loading="lazy"
                  width={448}
                  height={260}
                />
              </motion.div>
            </div>

            {/* Row 2: Image Left | Text Right */}
            <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <motion.div
                initial={{ opacity: 0, x: -80 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true }}
                className="flex justify-center"
              >
                <img
                  src="/Images/developer.png"
                  alt="Fiieve engineers building scalable software and engineering solutions"
                  className="w-full max-w-md rounded-lg border border-green-400/20 shadow-lg object-cover md:h-[260px] sm:h-[220px] h-[180px]"
                  loading="lazy"
                  width={448}
                  height={260}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 80 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true }}
                className={`${bodyText} leading-relaxed text-base md:text-lg`}
              >
                <p>
                  We believe in technology that empowers people, not complicates their work. Our
                  process emphasises collaboration, adaptability, and continuous improvement to
                  ensure the solutions we deliver make a meaningful difference.
                </p>
                <p className="mt-4">
                  Every line of code and design decision is driven by one principle: creating
                  digital experiences that are intuitive, efficient, and aligned with your
                  long-term vision. Whether you are a startup launching your first{" "}
                  <strong>MVP</strong> or an enterprise scaling your platform, Fiieve is your
                  end-to-end technology partner.
                </p>
              </motion.div>
            </div>
          </article>

          {/* ===== Sub-sections ===== */}
          {/* ✅ SEO: each sub-component should carry its own aria-labelledby heading */}
          <StatisticsSection />
          <MissionVisionValues />
          <MilestonesAchievements />
          <TeamSection />
          <HowItWorks />

        </div>
      </main>
    </>
  );
}